import { useParams, useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Link } from "react-router-dom";
import { useAccountUsers } from "../lib/hooks/useAccountUsers";
import { useEvent } from "../lib/hooks/useEvents";
import { EventInfoCard } from "../components/events/EventInfoCard";
import { RegistrationForm } from "../components/events/RegistrationForm";
import { RaceRegistrationForm } from "../components/events/RaceRegistrationForm";
import {
  useEnsureCheckout,
  useAddCheckoutItem,
  isRecoverableCheckoutSessionError,
  createNewCheckoutAndSyncStore,
  isCheckoutOwnershipError,
} from "../lib/basket-api";
import { useCheckoutStore } from "../stores/checkout-store";
import { handleApiError, showErrorToast, showSuccessToast } from "../lib/error-handling";
import {
  isBookingOpen,
  getAvailablePricePoints,
  getAllPricePoints,
} from "../lib/utils";
import { getTagVariant } from "../lib/constants";
import type { RegistrationFormData } from "../lib/schemas/registration";
import type { RaceRegistrationFormData } from "../lib/schemas/race-registration";

import { TEXT_CONTENT } from "../lib/text-content";

export const EventDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAddingToBasket, setIsAddingToBasket] = useState(false);

  const allAccountUsers = useAccountUsers();

  // Fetch event using custom hook
  const { data: eventResponse, isLoading, error } = useEvent(id);
  const event = eventResponse?.event;

  // Basket API hooks
  const { ensureCheckout } = useEnsureCheckout();
  const addCheckoutItem = useAddCheckoutItem();
  const { clearCheckout } = useCheckoutStore();

  // Bookable tiers only (excludes full) — totals, validation, basket
  const availablePricePoints = useMemo(() => {
    if (!event || event.isCancelled) return [];
    if (!event.pricePoints) return [];
    return getAvailablePricePoints(
      event.pricePoints,
      event.bookingClosesAt ?? null,
      event.startsAt,
    );
  }, [event]);

  // All tiers in the current booking window for the category dropdown (includes full, disabled)
  const categoryPricePoints = useMemo(() => {
    if (!event || event.isCancelled || !event.pricePoints?.length) return [];
    const { available } = getAllPricePoints(
      event.pricePoints,
      event.bookingClosesAt ?? null,
      event.startsAt,
    );
    return [...available].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
  }, [event]);

  // Check if booking is open
  const bookingOpen = useMemo(() => {
    if (!event) return false;
    if (event.isCancelled) return false;
    return isBookingOpen(event.bookingClosesAt ?? null, event.startsAt);
  }, [event]);

  const isRaceEvent = event != null && (event as { eventKind?: string }).eventKind === "RACE";

  const handleAddToBasket = async (data: RegistrationFormData, stayOnPage: boolean = false) => {
    if (!event) return;

    setIsAddingToBasket(true);
    try {
      const currentCheckoutId = await ensureCheckout();

      const addAllParticipants = async (checkoutIdForAdd: string) => {
        for (const participant of data.participants) {
          await addCheckoutItem.mutateAsync({
            params: { path: { checkoutId: checkoutIdForAdd } },
            body: {
              type: "EVENT",
              eventId: event.id,
              eventPricePointId: participant.pricePointId,
              firstName: participant.firstName.trim(),
              lastName: participant.lastName.trim(),
              userId: participant.userId === "other" ? undefined : participant.userId,
            },
          });
        }
      };

      try {
        await addAllParticipants(currentCheckoutId);
      } catch (addError) {
        if (isRecoverableCheckoutSessionError(addError)) {
          const newCheckoutId = await createNewCheckoutAndSyncStore(queryClient);
          if (!newCheckoutId) {
            showErrorToast(handleApiError(addError));
            return;
          }
          await addAllParticipants(newCheckoutId);
        } else {
          throw addError;
        }
      }

      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey.length >= 2 &&
            queryKey[0] === "get" &&
            typeof queryKey[1] === "string" &&
            queryKey[1].includes("/api/public/checkouts/")
          );
        },
      });

      const needsConsent =
        event.requiresConsentForm || event.requiresPhotoConsent;
      const consentMessage = needsConsent
        ? " Please complete the consent forms before checkout."
        : "";

      showSuccessToast(
        `Added ${data.quantity} ${data.quantity === 1 ? "participant" : "participants"} to basket.${consentMessage}`,
      );

      if (!stayOnPage) {
        navigate("/basket");
      }
    } catch (error) {
      if (isCheckoutOwnershipError(error)) {
        clearCheckout();
      } else {
        const apiError = handleApiError(error);
        showErrorToast(apiError);
      }
    } finally {
      setIsAddingToBasket(false);
    }
  };

  const handleAddRaceToBasket = async (
    data: RaceRegistrationFormData,
    stayOnPage: boolean = false,
  ) => {
    if (!event) return;

    setIsAddingToBasket(true);
    try {
      const currentCheckoutId = await ensureCheckout();

      // API accepts RACE type; generated api-types may only include EVENT until regenerated
      const raceBody = {
        type: "RACE" as const,
        eventId: event.id,
        eventPricePointId: data.eventPricePointId,
        boatClass: data.boatClass.trim(),
        sailNumber: data.sailNumber.trim(),
        crew: data.crew.map((c) => ({
          firstName: c.firstName.trim(),
          lastName: c.lastName.trim(),
          userId: c.userId && c.userId !== "other" ? c.userId : undefined,
        })),
      };
      await addCheckoutItem.mutateAsync({
        params: { path: { checkoutId: currentCheckoutId } },
        body: raceBody as unknown as Parameters<
          ReturnType<typeof useAddCheckoutItem>["mutateAsync"]
        >[0]["body"],
      });

      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey.length >= 2 &&
            queryKey[0] === "get" &&
            typeof queryKey[1] === "string" &&
            queryKey[1].includes("/api/public/checkouts/")
          );
        },
      });

      const needsConsent =
        event.requiresConsentForm || event.requiresPhotoConsent;
      const consentMessage = needsConsent
        ? " Please complete the consent forms before checkout."
        : "";

      showSuccessToast(`Added boat to basket.${consentMessage}`);

      if (!stayOnPage) {
        navigate("/basket");
      }
    } catch (error) {
      if (isCheckoutOwnershipError(error)) {
        clearCheckout();
      } else {
        const apiError = handleApiError(error);
        showErrorToast(apiError);
      }
    } finally {
      setIsAddingToBasket(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">{TEXT_CONTENT.EVENTS.LOADING}</span>
      </div>
    );
  }

  // Show error state
  if (error || !event) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {TEXT_CONTENT.EVENTS.NOT_FOUND_TITLE}
        </h3>
        <p className="text-gray-600">
          {error ? handleApiError(error).message : TEXT_CONTENT.EVENTS.NOT_FOUND_DESC}
        </p>
        <Button asChild className="mt-4">
          <Link to="/events">{TEXT_CONTENT.EVENTS.BACK_TO_EVENTS}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Back Button */}
      <Link to="/events">
        <Button variant="outline" className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          {TEXT_CONTENT.EVENTS.BACK_TO_EVENTS}
        </Button>
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <EventInfoCard event={event} getTagVariant={getTagVariant} />
        </div>

        {/* Registration Form */}
        <div className="lg:col-span-1">
          {isRaceEvent ? (
            <RaceRegistrationForm
              event={event}
              allAccountUsers={allAccountUsers}
              availablePricePoints={availablePricePoints}
              bookingOpen={bookingOpen}
              onSubmit={handleAddRaceToBasket}
              isSubmitting={isAddingToBasket}
              currency={event.pricePoints?.[0]?.currency || "gbp"}
            />
          ) : (
            <RegistrationForm
              event={event}
              allAccountUsers={allAccountUsers}
              availablePricePoints={availablePricePoints}
              categoryPricePoints={categoryPricePoints}
              bookingOpen={bookingOpen}
              onSubmit={handleAddToBasket}
              isSubmitting={isAddingToBasket}
              currency={event.pricePoints?.[0]?.currency || "gbp"}
            />
          )}
        </div>
      </div>
    </div>
  );
};
